// IndexedDB-backed offline write queue for MinePro.
// Each queued mutation carries an `attempts` counter and a `failed` flag so
// server-side rejections (4xx/5xx) are surfaced to the user instead of being
// silently dropped, and network errors trigger exponential backoff on reconnect.
const DB_NAME = "minepro-offline";
const STORE = "queue";
const DB_VERSION = 2;
const MAX_ATTEMPTS = 5;

let dbPromise = null;
function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("IndexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function withStore(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const req = fn(store);
    tx.oncomplete = () => resolve(req?.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

const listeners = new Set();
export function subscribeQueue(cb) {
  listeners.add(cb);
  listQueue().then((items) => cb(summarize(items))).catch(() => cb({ pending: 0, failed: 0, total: 0 }));
  return () => listeners.delete(cb);
}
function summarize(items) {
  const failed = items.filter((i) => i.failed).length;
  const pending = items.length - failed;
  return { pending, failed, total: items.length };
}
async function notify() {
  try {
    const items = await listQueue();
    const summary = summarize(items);
    listeners.forEach((cb) => cb(summary));
  } catch (_err) { /* noop */ }
}

export async function listQueue() {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (_err) { return []; }
}

export async function enqueue(item) {
  const id = await withStore("readwrite", (s) =>
    s.add({
      ...item,
      attempts: 0,
      failed: false,
      last_error: "",
      created_at: new Date().toISOString(),
    })
  );
  notify();
  return id;
}

export async function removeItem(id) {
  await withStore("readwrite", (s) => s.delete(id));
  notify();
}

export async function clearQueue() {
  await withStore("readwrite", (s) => s.clear());
  notify();
}

export async function clearFailed() {
  const items = await listQueue();
  for (const it of items) if (it.failed) await removeItem(it.id);
}

async function updateItem(id, patch) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const cur = getReq.result;
      if (!cur) return resolve(null);
      const next = { ...cur, ...patch };
      const putReq = store.put(next);
      putReq.onsuccess = () => resolve(next);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

/**
 * Force-retry a failed item: clears failed flag and attempts count so the
 * next drain picks it up again.
 */
export async function retryItem(id) {
  await updateItem(id, { failed: false, attempts: 0, last_error: "" });
  notify();
}

// ------------------------------------------------------------------
// Convenience wrappers used by Layout & MyShiftDialog
// ------------------------------------------------------------------
/**
 * Kick off background sync: try once if online, and listen for reconnect.
 * Idempotent — repeated calls attach only one listener per axios instance.
 */
let syncBound = false;
let periodicTimer = null;
export function initOfflineSync(axiosInstance) {
  if (typeof window === "undefined") return;
  const drain = () => { if (navigator.onLine) drainQueue(axiosInstance).catch(() => {}); };
  if (!syncBound) {
    window.addEventListener("online", drain);
    // Auto-drenaje periódico cada 60s: reintenta pendientes y también
    // fallidos "no-permanentes" (network glitches). Ligero — solo corre
    // si hay algo en la cola y hay red.
    periodicTimer = setInterval(async () => {
      if (!navigator.onLine) return;
      const items = await listQueue();
      if (items.length === 0) return;
      // Reactiva ítems fallidos que sean transientes (5xx / 408 / 429)
      for (const it of items) {
        if (it.failed && it.last_status && (it.last_status >= 500 || it.last_status === 408 || it.last_status === 429)) {
          await updateItem(it.id, { failed: false, last_error: "auto-retry" });
        }
      }
      drainQueue(axiosInstance).catch(() => {});
    }, 60_000);
    syncBound = true;
  }
  drain();
}

/** Subscribe only to the pending-count value (not the full summary). */
export function subscribePending(cb) {
  return subscribeQueue((s) => cb(s.pending + s.failed));
}

/** Manually flush the queue (used by the "offline badge" click). */
export async function flushQueue(axiosInstance) {
  return drainQueue(axiosInstance);
}

/** Queue a shift-capture payload as if it were a POST to /operations/entries. */
export async function enqueueShift(payload) {
  return enqueue({ method: "POST", url: "/operations/entries", data: payload });
}

let draining = false;
export async function drainQueue(axiosInstance) {
  if (draining) return { synced: 0, failed: 0, remaining: 0 };
  draining = true;
  let synced = 0;
  let failed = 0;
  try {
    const items = await listQueue();
    for (const item of items) {
      if (item.failed) continue;                      // needs manual retry
      if (!navigator.onLine) break;
      try {
        await axiosInstance.request({
          method: item.method,
          url: item.url,
          data: item.data,
          headers: { "X-Offline-Sync": "1" },
        });
        await removeItem(item.id);
        synced += 1;
      } catch (err) {
        const netErr =
          err?.code === "ERR_NETWORK" ||
          err?.message === "Network Error" ||
          !navigator.onLine;
        if (netErr) {
          // Transient — leave for next reconnect.
          break;
        }
        const attempts = (item.attempts || 0) + 1;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.message || "Error";
        const isPermanent = status && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
        if (isPermanent || attempts >= MAX_ATTEMPTS) {
          await updateItem(item.id, {
            failed: true, attempts,
            last_error: typeof detail === "string" ? detail : JSON.stringify(detail),
            last_status: status || 0,
          });
          failed += 1;
        } else {
          await updateItem(item.id, {
            attempts,
            last_error: typeof detail === "string" ? detail : String(detail),
            last_status: status || 0,
          });
        }
      }
    }
  } finally {
    draining = false;
  }
  const all = await listQueue();
  notify();
  return { synced, failed, remaining: all.filter((i) => !i.failed).length, failed_count: all.filter((i) => i.failed).length };
}
